<script lang="ts">
	import { _ } from 'svelte-i18n';
	import { fade, fly, scale } from 'svelte/transition';
	import { onMount } from 'svelte';
	import Header from '$lib/components/sections/Header.svelte';
	import Footer from '$lib/components/sections/Footer.svelte';
	import SeoContent from '$lib/components/sections/SeoContent.svelte';
	import Button from '$lib/components/primitives/Button.svelte';

	import Icon from '$lib/components/primitives/Icon.svelte';

	let visible = $state(true); // SSR: start visible so hero + <h1> are in server HTML (Ahrefs: "H1 tag missing"). onMount re-set is a no-op; entrance animations still play on client-side navigation.
	let currentEmail = $state(0);
	let inboxCount = $state(47);

	onMount(() => {
		visible = true;
		// Simulate inbox overwhelm
		const emailInterval = setInterval(() => {
			currentEmail = (currentEmail + 1) % emailChain.length;
		}, 2000);
		// Simulate growing inbox
		const countInterval = setInterval(() => {
			inboxCount = Math.min(inboxCount + Math.floor(Math.random() * 3), 99);
		}, 3000);
		return () => {
			clearInterval(emailInterval);
			clearInterval(countInterval);
		};
	});

	const emailPains = [
		{
			title: $_('features.communication.emailPains.pain1.title'),
			scenario: $_('features.communication.emailPains.pain1.scenario'),
			description: $_('features.communication.emailPains.pain1.description'),
			solution: $_('features.communication.emailPains.pain1.solution')
		},
		{
			title: $_('features.communication.emailPains.pain2.title'),
			scenario: $_('features.communication.emailPains.pain2.scenario'),
			description: $_('features.communication.emailPains.pain2.description'),
			solution: $_('features.communication.emailPains.pain2.solution')
		},
		{
			title: $_('features.communication.emailPains.pain3.title'),
			scenario: $_('features.communication.emailPains.pain3.scenario'),
			description: $_('features.communication.emailPains.pain3.description'),
			solution: $_('features.communication.emailPains.pain3.solution')
		}
	];

	const emailChain = [
		{
			sender: $_('features.communication.emailChain.email1.sender'),
			subject: $_('features.communication.emailChain.email1.subject'),
			time: $_('features.communication.emailChain.email1.time')
		},
		{
			sender: $_('features.communication.emailChain.email2.sender'),
			subject: $_('features.communication.emailChain.email2.subject'),
			time: $_('features.communication.emailChain.email2.time')
		},
		{
			sender: $_('features.communication.emailChain.email3.sender'),
			subject: $_('features.communication.emailChain.email3.subject'),
			time: $_('features.communication.emailChain.email3.time')
		},
		{
			sender: $_('features.communication.emailChain.email4.sender'),
			subject: $_('features.communication.emailChain.email4.subject'),
			time: $_('features.communication.emailChain.email4.time')
		},
		{
			sender: $_('features.communication.emailChain.email5.sender'),
			subject: $_('features.communication.emailChain.email5.subject'),
			time: $_('features.communication.emailChain.email5.time')
		}
	];

	const solutions = [
		{
			icon: 'message-square',
			title: $_('features.communication.solutions.solution1.title'),
			description: $_('features.communication.solutions.solution1.description'),
			benefit: $_('features.communication.solutions.solution1.benefit')
		},
		{
			icon: 'bell',
			title: $_('features.communication.solutions.solution2.title'),
			description: $_('features.communication.solutions.solution2.description'),
			benefit: $_('features.communication.solutions.solution2.benefit')
		},
		{
			icon: 'file-text',
			title: $_('features.communication.solutions.solution3.title'),
			description: $_('features.communication.solutions.solution3.description'),
			benefit: $_('features.communication.solutions.solution3.benefit')
		},
		{
			icon: 'users',
			title: $_('features.communication.solutions.solution4.title'),
			description: $_('features.communication.solutions.solution4.description'),
			benefit: $_('features.communication.solutions.solution4.benefit')
		}
	];
</script>

<svelte:head>
	<title>{$_('features.communication.meta.title')}</title>
	<meta name="description" content={$_('features.communication.meta.description')} />
	<meta property="og:title" content="{$_('features.communication.meta.title')}" />
	<meta property="og:description" content={$_('features.communication.meta.description')} />
</svelte:head>

<Header />

<div class="feature-page">
	<!-- Hero Section -->
	<section class="hero">
		<div class="container">
			{#if visible}
				<div class="hero__split">
					<div class="hero__content" in:fade={{ duration: 600, delay: 100 }}>
						<div class="hero__frustration" in:fly={{ y: 30, duration: 600, delay: 200 }}>
							<p class="hero__complaint">{$_('features.communication.hero.complaint')}</p>
						</div>
						<h1 class="hero__title" in:fly={{ y: 30, duration: 600, delay: 300 }}>
							{@html $_('features.communication.hero.title')}
						</h1>
						<p class="hero__subtitle" in:fly={{ y: 30, duration: 600, delay: 400 }}>
							{$_('features.communication.hero.subtitle')}
						</p>
						<div class="hero__actions" in:fly={{ y: 30, duration: 600, delay: 500 }}>
							<Button variant="primary" size="lg" href="https://app.heyweek.com/?signup">
								{$_('features.communication.hero.primaryCta')}
							</Button>
							<Button variant="ghost" size="lg" href="#email-pains">
								{$_('features.communication.hero.secondaryCta')}
							</Button>
						</div>
					</div>
					<div class="hero__visual" in:scale={{ duration: 800, delay: 400 }}>
						<div class="email-chaos">
							<div class="inbox-header">
								<Icon name="inbox" size={20} />
								<span>{$_('features.communication.emailWidget.inbox')} ({inboxCount})</span>
								<div class="stress-indicator">
									<Icon name="alert-circle" size={16} style="color: #ef4444;" />
								</div>
							</div>
							<div class="email-list">
								{#each emailChain as email, index}
									<div class="email-item" class:active={index === currentEmail}>
										<div class="email-item__dot unread"></div>
										<div class="email-item__content">
											<div class="email-item__sender">{email.sender}</div>
											<div class="email-item__subject">{email.subject}</div>
											<div class="email-item__time">{email.time}</div>
										</div>
									</div>
								{/each}
								<div class="email-overflow">
									<div class="email-overflow__text">
										+{inboxCount - 5}
										{$_('features.communication.emailWidget.moreEmails')}
									</div>
									<div class="email-overflow__stress">😵‍💫</div>
								</div>
							</div>
						</div>
					</div>
				</div>
			{/if}
		</div>
	</section>

	<!-- Email Pains -->
	<section class="email-pains" id="email-pains">
		<div class="container">
			<div class="email-pains__header">
				<h2 class="email-pains__title">{$_('features.communication.emailPains.title')}</h2>
				<p class="email-pains__subtitle">{$_('features.communication.emailPains.subtitle')}</p>
			</div>
			<div class="pains-list">
				{#each emailPains as pain, index}
					<div class="pain-story" style="animation-delay: {index * 400}ms">
						<div class="pain-story__header">
							<h3 class="pain-story__title">{pain.title}</h3>
							<div class="pain-story__scenario">{pain.scenario}</div>
						</div>
						<div class="pain-story__problem">
							<Icon name="frown" size={24} style="color: #ef4444;" />
							<p>{pain.description}</p>
						</div>
						<div class="pain-story__divider"></div>
						<div class="pain-story__solution">
							<Icon name="lightbulb" size={24} style="color: rgb(255, 94, 126);" />
							<p>{pain.solution}</p>
						</div>
					</div>
				{/each}
			</div>
		</div>
	</section>

	<!-- Solutions Section -->
	<section class="solutions">
		<div class="container">
			<div class="solutions__header">
				<h2 class="solutions__title">{$_('features.communication.solutions.title')}</h2>
				<p class="solutions__subtitle">{$_('features.communication.solutions.subtitle')}</p>
			</div>
			<div class="solutions__grid">
				{#each solutions as solution, index}
					<div class="solution-block" style="animation-delay: {index * 200}ms">
						<div class="solution-block__icon">
							<Icon name={solution.icon} size={32} style="color: rgb(255, 94, 126);" />
						</div>
						<h3 class="solution-block__title">{solution.title}</h3>
						<p class="solution-block__description">{solution.description}</p>
						<div class="solution-block__benefit">
							<Icon name="arrow-right" size={16} style="color: #10b981;" />
							<span>{solution.benefit}</span>
						</div>
					</div>
				{/each}
			</div>
		</div>
	</section>

	<!-- Transformation Section -->
	<section class="transformation">
		<div class="container">
			<div class="transformation__comparison">
				<div class="transformation__before">
					<h3 class="transformation__label">
						<Icon name="x-circle" size={24} style="color: #ef4444;" />
						{$_('features.communication.transformation.before.label')}
					</h3>
					<div class="email-simulation">
						<div class="email-thread">
							<div class="email-message">
								{$_('features.communication.transformation.before.message1')}
							</div>
							<div class="email-message">
								{$_('features.communication.transformation.before.message2')}
							</div>
							<div class="email-message">
								{$_('features.communication.transformation.before.message3')}
							</div>
							<div class="email-message">
								{$_('features.communication.transformation.before.message4')}
							</div>
							<div class="email-message confused">
								{$_('features.communication.transformation.before.message5')}
							</div>
						</div>
						<div class="chaos-indicator">
							{$_('features.communication.transformation.before.indicator')}
						</div>
					</div>
				</div>
				<div class="transformation__arrow">
					<Icon name="arrow-right" size={32} style="color: rgb(255, 94, 126);" />
				</div>
				<div class="transformation__after">
					<h3 class="transformation__label">
						<Icon name="check-circle" size={24} style="color: rgb(255, 94, 126);" />
						{$_('features.communication.transformation.after.label')}
					</h3>
					<div class="project-channel">
						<div class="channel-header">
							<Icon name="hash" size={16} />
							project-x-budget
						</div>
						<div class="channel-messages">
							<div class="channel-message">
								{$_('features.communication.transformation.after.message1')}
							</div>
							<div class="channel-message">
								{$_('features.communication.transformation.after.message2')}
							</div>
							<div class="channel-message decision">
								{$_('features.communication.transformation.after.message3')}
							</div>
						</div>
						<div class="efficiency-indicator">
							{$_('features.communication.transformation.after.indicator')}
						</div>
					</div>
				</div>
			</div>
		</div>
	</section>

	<!-- CTA Section -->
	<section class="cta">
		<div class="container">
			<div class="cta__content">
				<h2 class="cta__title">{$_('features.communication.cta.title')}</h2>
				<p class="cta__subtitle">
					{$_('features.communication.cta.subtitle')}
				</p>
				<div class="cta__actions">
					<Button variant="primary" size="lg" href="https://app.heyweek.com/?signup">
						{$_('features.communication.cta.primaryButton')}
					</Button>
					<Button variant="ghost" size="lg" href="/pricing">
						{$_('features.communication.cta.secondaryButton')}
					</Button>
				</div>
			</div>
		</div>
	</section>
</div>

<SeoContent slug="communication" />

<Footer />

<style>
	.feature-page {
		min-height: 100vh;
	}

	/* Hero Section - Split with inbox simulation */
	.hero {
		padding: var(--space-20) 0;
		background: linear-gradient(
			135deg,
			rgba(239, 68, 68, 0.03) 0%,
			transparent 70%,
			rgba(255, 94, 126, 0.05) 100%
		);
	}

	.hero__split {
		display: grid;
		grid-template-columns: 1fr 1fr;
		gap: var(--space-16);
		align-items: center;
		max-width: 1200px;
		margin: 0 auto;
	}

	.hero__frustration {
		margin-bottom: var(--space-6);
	}

	.hero__complaint {
		font-size: var(--font-size-lg);
		color: var(--text-secondary);
		font-style: italic;
		margin: 0;
	}

	.hero__title {
		font-size: var(--font-size-5xl);
		font-weight: var(--font-weight-bold);
		line-height: 1.1;
		color: var(--text-primary);
		margin-bottom: var(--space-6);
		letter-spacing: -0.02em;
	}

	@keyframes underlineGrow {
		0%,
		100% {
			width: 100%;
		}
		50% {
			width: 120%;
		}
	}

	.hero__subtitle {
		font-size: var(--font-size-lg);
		color: var(--text-secondary);
		line-height: var(--line-height-relaxed);
		margin-bottom: var(--space-8);
	}

	.hero__actions {
		display: flex;
		gap: var(--space-4);
		flex-wrap: wrap;
	}

	/* Email Chaos Simulation */
	.email-chaos {
		max-width: 400px;
		margin: 0 auto;
		background: var(--bg-primary);
		border: 1px solid var(--border-light);
		border-radius: var(--radius-lg);
		box-shadow: var(--shadow-xl);
		overflow: hidden;
	}

	.inbox-header {
		display: flex;
		align-items: center;
		gap: var(--space-3);
		padding: var(--space-4);
		background: var(--bg-secondary);
		border-bottom: 1px solid var(--border-light);
		font-weight: var(--font-weight-medium);
		color: var(--text-primary);
	}

	.stress-indicator {
		margin-left: auto;
		animation: pulse 1.5s ease-in-out infinite;
	}

	@keyframes pulse {
		0%,
		100% {
			opacity: 1;
		}
		50% {
			opacity: 0.5;
		}
	}

	.email-list {
		padding: var(--space-2);
	}

	.email-item {
		display: flex;
		align-items: center;
		gap: var(--space-3);
		padding: var(--space-3);
		border-radius: var(--radius-md);
		margin-bottom: var(--space-2);
		transition: all 0.3s ease;
		opacity: 0.7;
	}

	.email-item.active {
		background: rgba(239, 68, 68, 0.1);
		border: 1px solid rgba(239, 68, 68, 0.2);
		opacity: 1;
		transform: scale(1.02);
	}

	.email-item__dot {
		width: 8px;
		height: 8px;
		border-radius: var(--radius-full);
		flex-shrink: 0;
	}

	.email-item__dot.unread {
		background: #ef4444;
	}

	.email-item__content {
		flex: 1;
		min-width: 0;
	}

	.email-item__sender {
		font-size: var(--font-size-sm);
		font-weight: var(--font-weight-medium);
		color: var(--text-primary);
	}

	.email-item__subject {
		font-size: var(--font-size-xs);
		color: var(--text-secondary);
		white-space: nowrap;
		overflow: hidden;
		text-overflow: ellipsis;
		margin: var(--space-1) 0;
	}

	.email-item__time {
		font-size: var(--font-size-xs);
		color: var(--text-tertiary);
	}

	.email-overflow {
		padding: var(--space-4);
		text-align: center;
		border-top: 1px solid var(--border-light);
		background: rgba(239, 68, 68, 0.05);
	}

	.email-overflow__text {
		font-size: var(--font-size-sm);
		color: var(--text-secondary);
		margin-bottom: var(--space-2);
	}

	.email-overflow__stress {
		font-size: var(--font-size-xl);
		animation: spin 3s linear infinite;
	}

	@keyframes spin {
		from {
			transform: rotate(0deg);
		}
		to {
			transform: rotate(360deg);
		}
	}

	/* Email Pains Section */
	.email-pains {
		padding: var(--space-20) 0;
		background: var(--bg-secondary);
	}

	.email-pains__header {
		text-align: center;
		margin-bottom: var(--space-16);
	}

	.email-pains__title {
		font-size: var(--font-size-3xl);
		font-weight: var(--font-weight-semibold);
		color: var(--text-primary);
		margin-bottom: var(--space-4);
		letter-spacing: -0.02em;
	}

	.email-pains__subtitle {
		font-size: var(--font-size-lg);
		color: var(--text-secondary);
	}

	.pains-list {
		max-width: 1000px;
		margin: 0 auto;
		display: flex;
		flex-direction: column;
		gap: var(--space-12);
	}

	.pain-story {
		background: var(--bg-primary);
		border: 1px solid var(--border-light);
		border-radius: var(--radius-lg);
		padding: var(--space-8);
		animation: slideUp 0.6s ease-out both;
	}

	@keyframes slideUp {
		from {
			opacity: 0;
			transform: translateY(30px);
		}
		to {
			opacity: 1;
			transform: translateY(0);
		}
	}

	.pain-story__header {
		margin-bottom: var(--space-6);
	}

	.pain-story__title {
		font-size: var(--font-size-xl);
		font-weight: var(--font-weight-semibold);
		color: var(--text-primary);
		margin: 0 0 var(--space-2) 0;
		letter-spacing: -0.02em;
	}

	.pain-story__scenario {
		font-size: var(--font-size-base);
		color: #ef4444;
		font-weight: var(--font-weight-medium);
		font-family: 'Courier New', monospace;
		background: rgba(239, 68, 68, 0.1);
		padding: var(--space-2) var(--space-3);
		border-radius: var(--radius-sm);
		border-left: 3px solid #ef4444;
	}

	.pain-story__problem {
		display: flex;
		gap: var(--space-4);
		align-items: flex-start;
		margin-bottom: var(--space-6);
	}

	.pain-story__problem p {
		font-size: var(--font-size-base);
		color: var(--text-secondary);
		line-height: 1.6;
		margin: 0;
		font-style: italic;
	}

	.pain-story__divider {
		height: 1px;
		background: linear-gradient(to right, transparent, var(--border-light), transparent);
		margin: var(--space-6) 0;
	}

	.pain-story__solution {
		display: flex;
		gap: var(--space-4);
		align-items: flex-start;
	}

	.pain-story__solution p {
		font-size: var(--font-size-base);
		color: var(--text-primary);
		line-height: 1.6;
		margin: 0;
	}

	/* Solutions Section */
	.solutions {
		padding: var(--space-20) 0;
	}

	.solutions__header {
		text-align: center;
		margin-bottom: var(--space-16);
	}

	.solutions__title {
		font-size: var(--font-size-3xl);
		font-weight: var(--font-weight-semibold);
		color: var(--text-primary);
		margin-bottom: var(--space-4);
		letter-spacing: -0.02em;
	}

	.solutions__subtitle {
		font-size: var(--font-size-lg);
		color: var(--text-secondary);
	}

	.solutions__grid {
		display: grid;
		grid-template-columns: repeat(2, 1fr);
		gap: var(--space-8);
		max-width: 1000px;
		margin: 0 auto;
	}

	.solution-block {
		padding: var(--space-8);
		background: var(--bg-primary);
		border: 1px solid var(--border-light);
		border-radius: var(--radius-lg);
		transition: all 0.3s ease;
		animation: fadeInUp 0.6s ease-out both;
	}

	@keyframes fadeInUp {
		from {
			opacity: 0;
			transform: translateY(30px);
		}
		to {
			opacity: 1;
			transform: translateY(0);
		}
	}

	.solution-block:hover {
		box-shadow: var(--shadow-lg);
		transform: translateY(-4px);
		border-color: rgba(255, 94, 126, 0.3);
	}

	.solution-block__icon {
		width: 60px;
		height: 60px;
		background: rgba(255, 94, 126, 0.1);
		border-radius: var(--radius-lg);
		display: flex;
		align-items: center;
		justify-content: center;
		margin-bottom: var(--space-4);
	}

	.solution-block__title {
		font-size: var(--font-size-xl);
		font-weight: var(--font-weight-semibold);
		color: var(--text-primary);
		margin: 0 0 var(--space-3) 0;
	}

	.solution-block__description {
		font-size: var(--font-size-base);
		color: var(--text-secondary);
		line-height: 1.6;
		margin: 0 0 var(--space-4) 0;
	}

	.solution-block__benefit {
		display: flex;
		align-items: center;
		gap: var(--space-2);
		font-size: var(--font-size-sm);
		color: var(--text-primary);
		font-weight: var(--font-weight-medium);
		padding: var(--space-2) 0;
		border-top: 1px solid var(--border-light);
	}

	/* Transformation Section */
	.transformation {
		padding: var(--space-20) 0;
		background: linear-gradient(135deg, rgba(239, 68, 68, 0.03) 0%, rgba(255, 94, 126, 0.05) 100%);
	}

	.transformation__comparison {
		display: grid;
		grid-template-columns: 1fr auto 1fr;
		gap: var(--space-8);
		align-items: center;
		max-width: 1200px;
		margin: 0 auto;
	}

	.transformation__label {
		display: flex;
		align-items: center;
		gap: var(--space-3);
		font-size: var(--font-size-xl);
		font-weight: var(--font-weight-semibold);
		color: var(--text-primary);
		margin: 0 0 var(--space-6) 0;
		justify-content: center;
		letter-spacing: -0.02em;
	}

	.transformation__arrow {
		display: flex;
		align-items: center;
		justify-content: center;
		width: 60px;
		height: 60px;
		background: var(--bg-primary);
		border: 2px solid rgba(255, 94, 126, 0.3);
		border-radius: var(--radius-full);
		box-shadow: var(--shadow-md);
	}

	/* Email Simulation */
	.email-simulation {
		padding: var(--space-6);
		background: var(--bg-primary);
		border: 1px solid rgba(239, 68, 68, 0.2);
		border-radius: var(--radius-lg);
	}

	.email-thread {
		margin-bottom: var(--space-4);
	}

	.email-message {
		padding: var(--space-3) var(--space-4);
		background: var(--bg-secondary);
		border: 1px solid var(--border-light);
		border-radius: var(--radius-md);
		margin-bottom: var(--space-2);
		font-size: var(--font-size-sm);
		color: var(--text-primary);
	}

	.email-message.confused {
		background: rgba(239, 68, 68, 0.1);
		border-color: rgba(239, 68, 68, 0.3);
		color: #ef4444;
		font-style: italic;
	}

	.chaos-indicator {
		font-size: var(--font-size-xs);
		color: #ef4444;
		text-align: center;
		font-weight: var(--font-weight-medium);
		padding: var(--space-2);
		background: rgba(239, 68, 68, 0.1);
		border-radius: var(--radius-sm);
	}

	/* Project Channel Simulation */
	.project-channel {
		padding: var(--space-6);
		background: var(--bg-primary);
		border: 1px solid rgba(255, 94, 126, 0.2);
		border-radius: var(--radius-lg);
	}

	.channel-header {
		display: flex;
		align-items: center;
		gap: var(--space-2);
		padding: var(--space-3) var(--space-4);
		background: rgba(255, 94, 126, 0.1);
		border-radius: var(--radius-md);
		font-size: var(--font-size-sm);
		font-weight: var(--font-weight-medium);
		color: rgb(255, 94, 126);
		margin-bottom: var(--space-4);
	}

	.channel-messages {
		margin-bottom: var(--space-4);
	}

	.channel-message {
		padding: var(--space-3) var(--space-4);
		background: var(--bg-secondary);
		border: 1px solid var(--border-light);
		border-radius: var(--radius-md);
		margin-bottom: var(--space-2);
		font-size: var(--font-size-sm);
		color: var(--text-primary);
	}

	.channel-message.decision {
		background: rgba(16, 185, 129, 0.1);
		border-color: rgba(16, 185, 129, 0.3);
		color: #10b981;
		font-weight: var(--font-weight-medium);
	}

	.efficiency-indicator {
		font-size: var(--font-size-xs);
		color: rgb(255, 94, 126);
		text-align: center;
		font-weight: var(--font-weight-medium);
		padding: var(--space-2);
		background: rgba(255, 94, 126, 0.1);
		border-radius: var(--radius-sm);
	}

	/* CTA Section */
	.cta {
		padding: var(--space-20) 0;
		text-align: center;
		background: linear-gradient(180deg, transparent 0%, var(--bg-secondary) 100%);
	}

	.cta__content {
		max-width: 700px;
		margin: 0 auto;
	}

	.cta__title {
		font-size: var(--font-size-3xl);
		font-weight: var(--font-weight-semibold);
		color: var(--text-primary);
		margin-bottom: var(--space-4);
		letter-spacing: -0.02em;
	}

	.cta__subtitle {
		font-size: var(--font-size-lg);
		color: var(--text-secondary);
		margin-bottom: var(--space-8);
		line-height: 1.6;
	}

	.cta__actions {
		display: flex;
		gap: var(--space-4);
		justify-content: center;
		flex-wrap: wrap;
	}

	/* Responsive */
	@media (max-width: 768px) {
		.hero__split {
			grid-template-columns: 1fr;
			gap: var(--space-8);
		}

		.hero__title {
			font-size: 2rem;
		}

		.email-chaos {
			order: -1;
		}

		.solutions__grid {
			grid-template-columns: 1fr;
		}

		.transformation__comparison {
			grid-template-columns: 1fr;
			gap: var(--space-6);
		}

		.transformation__arrow {
			transform: rotate(90deg);
			justify-self: center;
		}
	}
</style>
